// CROSSFIRE game title introduction.
// A cinematic layered composition, deliberately NOT neon:
//  - Deep near-black backdrop with slow-moving haze
//  - Two operator silhouettes low in the frame
//  - The CROSSFIRE title assembles in letter groups with subtle
//    fragmentation, then a light sweep travels across the letters
//  - Small "BETA" tag and "Developed by AXIS" caption below
//  - Optional subtle camera push (scale) toward the logo
//
// Kept modular: caller drives the phase timing ("in"|"hold"|"out").
import { useEffect, useMemo, useState } from "react";
import { BRAND, INTRO_TIMING } from "@/config/game";
import { Audio_ } from "@/lib/audio";
import IntroCinematic from "@/components/IntroCinematic";

// Split title into letter groups so we can stagger their reveal.
function splitToGroups(title) {
    // Choose group boundaries that feel balanced for common titles
    const len = title.length;
    if (len <= 3) return [title.split("")];
    if (len <= 6) return [title.slice(0, Math.ceil(len / 2)), title.slice(Math.ceil(len / 2))];
    const a = Math.floor(len / 3);
    const b = Math.floor((2 * len) / 3);
    return [title.slice(0, a), title.slice(a, b), title.slice(b)];
}

/**
 * @param {"in"|"hold"|"out"} phase
 */
export default function CrossfireTitle({ phase }) {
    const groups = useMemo(() => splitToGroups(BRAND.game.title), []);
    const [swept, setSwept] = useState(false);
    const [flash, setFlash] = useState(0);
    // Light sweep trigger during "hold"
    useEffect(() => {
        if (phase === "hold") {
            const t = setTimeout(() => {
                setSwept(true);
                Audio_.play("titleSweep", { category: "music" });
            }, 350);
            // Occasional distant "flash"
            const f1 = setTimeout(() => setFlash(1), 800);
            const f1b = setTimeout(() => setFlash(0), 900);
            const f2 = setTimeout(() => setFlash(1), 1700);
            const f2b = setTimeout(() => setFlash(0), 1770);
            return () => {
                clearTimeout(t);
                clearTimeout(f1);
                clearTimeout(f1b);
                clearTimeout(f2);
                clearTimeout(f2b);
            };
        }
        if (phase === "in") setSwept(false);
    }, [phase]);

    const containerOpacity = phase === "out" ? 0 : 1;
    const cameraScale =
        phase === "in" ? 1.02 : phase === "hold" ? 1.04 : 1.08;

    return (
        <div
            data-testid="intro-game-title"
            className="absolute inset-0 flex items-center justify-center overflow-hidden"
            style={{
                opacity: containerOpacity,
                transition:
                    phase === "out"
                        ? `opacity ${INTRO_TIMING.GAME_TITLE_OUT}ms ease`
                        : "opacity 900ms ease",
            }}
        >
            {/* Layer 1: Environmental haze / vignette */}
            <Haze phase={phase} />

            {/* Layer 2: 3D cinematic scene (slow-panning operators + volumetric fog) */}
            <IntroCinematic active={phase === "in" || phase === "hold"} />

            {/* Occasional gunfire flash (subtle) */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse at 30% 78%, rgba(255,170,90,0.35), transparent 45%)",
                    opacity: flash * 0.5,
                    transition: "opacity 60ms linear",
                    mixBlendMode: "screen",
                }}
            />

            {/* Layer 4: Title */}
            <div
                className="relative z-10 flex flex-col items-center"
                style={{
                    transform: `scale(${cameraScale})`,
                    transition: `transform ${INTRO_TIMING.GAME_TITLE_IN + INTRO_TIMING.GAME_TITLE_HOLD}ms cubic-bezier(0.16, 1, 0.3, 1)`,
                }}
            >
                {/* Small header pretext */}
                <div
                    className="mb-6 flex items-center gap-3 opacity-0"
                    style={{
                        animation: phase === "in" ? "cfPretext 1200ms 200ms ease forwards" : undefined,
                        opacity: phase === "hold" || phase === "out" ? 1 : undefined,
                    }}
                >
                    <div className="h-px w-8 bg-white/25" />
                    <span
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.55em",
                            color: "rgba(255,255,255,0.42)",
                        }}
                    >
                        A TACTICAL SHOOTER
                    </span>
                    <div className="h-px w-8 bg-white/25" />
                </div>

                {/* Main title */}
                <div className="relative flex items-baseline">
                    {BRAND.game.titleImage ? (
                        <img
                            src={BRAND.game.titleImage}
                            alt={BRAND.game.title}
                            className="w-[720px] max-w-[92vw]"
                        />
                    ) : (
                        <TitleText phase={phase} groups={groups} swept={swept} />
                    )}
                </div>

                {/* Bottom line: BETA + credit */}
                <div
                    className="mt-8 flex items-center gap-6"
                    style={{
                        opacity: phase === "hold" || phase === "out" ? 1 : 0,
                        transform:
                            phase === "hold" || phase === "out" ? "translateY(0)" : "translateY(8px)",
                        transition: "opacity 900ms ease 400ms, transform 900ms ease 400ms",
                    }}
                >
                    <span
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 11,
                            letterSpacing: "0.55em",
                            color: "rgba(255,255,255,0.55)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            padding: "4px 10px",
                        }}
                    >
                        {BRAND.game.subtitle}
                    </span>
                    <span
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.5em",
                            color: "rgba(255,255,255,0.35)",
                        }}
                    >
                        {BRAND.game.credit}
                    </span>
                </div>
            </div>

            {/* Local keyframes */}
            <style>{`
                @keyframes cfPretext { to { opacity: 1; } }
                @keyframes cfGroupIn {
                    0% { opacity: 0; transform: translateY(14px) scale(1.02); filter: blur(6px); }
                    60% { filter: blur(0.5px); }
                    100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                }
                @keyframes cfGlitch {
                    0%, 100% { transform: translate(0, 0); }
                    20% { transform: translate(1px, -1px); }
                    40% { transform: translate(-1px, 1px); }
                    60% { transform: translate(0.5px, 0); }
                }
                @keyframes cfDust {
                    0% { transform: translateY(0) translateX(0); opacity: 0; }
                    10% { opacity: 0.5; }
                    90% { opacity: 0.4; }
                    100% { transform: translateY(-100vh) translateX(40px); opacity: 0; }
                }
            `}</style>
        </div>
    );
}

function TitleText({ phase, groups, swept }) {
    // Renders each letter group with a stagger; overlays a moving sweep gradient
    // Using SVG mask for the light sweep gives crisp text with a "light passing across"
    let letterOffset = 0;
    return (
        <div
            className="relative flex items-baseline"
            style={{
                fontFamily: "'Teko', 'Rajdhani', 'Chakra Petch', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(72px, 12vw, 168px)",
                lineHeight: 0.9,
                letterSpacing: "0.06em",
            }}
        >
            {groups.map((group, gi) => (
                <span
                    key={gi}
                    style={{
                        opacity: phase === "in" ? 0 : 1,
                        animation:
                            phase === "in"
                                ? `cfGroupIn 900ms ${gi * 260 + 200}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`
                                : undefined,
                        display: "inline-flex",
                    }}
                >
                    {group.split("").map((ch, ci) => {
                        letterOffset += 1;
                        const idx = letterOffset;
                        return (
                            <span
                                key={ci}
                                style={{
                                    position: "relative",
                                    background:
                                        "linear-gradient(180deg, #ffffff 0%, #d5d9dd 45%, #7a8088 100%)",
                                    WebkitBackgroundClip: "text",
                                    backgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    // subtle metallic drop
                                    filter:
                                        "drop-shadow(0 2px 0 rgba(0,0,0,0.7)) drop-shadow(0 0 22px rgba(0,0,0,0.6))",
                                    animation:
                                        phase === "hold" && idx % 3 === 0
                                            ? `cfGlitch 3200ms ${idx * 220}ms ease-in-out infinite`
                                            : undefined,
                                }}
                            >
                                {ch}
                            </span>
                        );
                    })}
                </span>
            ))}

            {/* Light sweep overlay */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(105deg, transparent 40%, rgba(255,220,170,0.55) 50%, transparent 60%)",
                    mixBlendMode: "screen",
                    transform: swept ? "translateX(120%)" : "translateX(-120%)",
                    transition: swept ? "transform 1200ms cubic-bezier(0.35, 0, 0.25, 1)" : "none",
                }}
            />

            {/* Amber underline accent */}
            <div
                className="absolute left-0 right-0"
                style={{
                    bottom: "-18px",
                    height: 2,
                    background:
                        "linear-gradient(90deg, transparent, rgba(255,170,90,0.7), rgba(255,255,255,0.5), rgba(255,170,90,0.7), transparent)",
                    opacity: phase === "hold" || phase === "out" ? 1 : 0,
                    transform: swept ? "scaleX(1)" : "scaleX(0.2)",
                    transformOrigin: "center",
                    transition: "opacity 700ms 300ms ease, transform 1400ms 200ms cubic-bezier(0.16,1,0.3,1)",
                }}
            />
        </div>
    );
}

function Haze({ phase }) {
    return (
        <>
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 70%, rgba(255,170,90,0.06), transparent 55%)",
                    opacity: phase === "in" ? 0 : phase === "hold" ? 1 : 0.4,
                    transition: "opacity 1400ms ease",
                }}
            />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 30%, transparent 55%, rgba(0,0,0,0.85) 100%)",
                }}
            />
            {/* Dust particles */}
            <DustField />
        </>
    );
}

function DustField() {
    const dots = Array.from({ length: 28 });
    return (
        <div className="absolute inset-0 pointer-events-none">
            {dots.map((_, i) => {
                const left = (i * 43) % 100;
                const size = 1 + ((i * 5) % 2);
                const dur = 14 + ((i * 17) % 12);
                const delay = (i * 0.29) % 8;
                return (
                    <span
                        key={i}
                        style={{
                            position: "absolute",
                            left: `${left}%`,
                            bottom: "-10px",
                            width: size,
                            height: size,
                            background: "rgba(220, 200, 170, 0.55)",
                            borderRadius: "50%",
                            filter: "blur(0.6px)",
                            animation: `cfDust ${dur}s linear ${delay}s infinite`,
                        }}
                    />
                );
            })}
        </div>
    );
}

function TacticalBackdrop({ phase }) {
    // Distant "environment" — dim horizon line + faint building blocks
    return (
        <div
            className="absolute inset-0 pointer-events-none"
            style={{
                opacity: phase === "in" ? 0.4 : phase === "hold" ? 0.7 : 0.5,
                transition: "opacity 1600ms ease",
            }}
        >
            {/* Horizon */}
            <div
                className="absolute"
                style={{
                    left: 0,
                    right: 0,
                    bottom: "26%",
                    height: 1,
                    background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
                }}
            />
            {/* Distant structures */}
            <svg
                width="100%"
                height="100%"
                viewBox="0 0 1600 900"
                preserveAspectRatio="xMidYMax slice"
                style={{ position: "absolute", inset: 0, opacity: 0.4 }}
            >
                <g fill="#0a0d10" stroke="rgba(255,255,255,0.06)" strokeWidth="1">
                    <rect x="120" y="580" width="70" height="140" />
                    <rect x="190" y="600" width="110" height="120" />
                    <rect x="300" y="560" width="60" height="160" />
                    <rect x="360" y="590" width="90" height="130" />
                    <rect x="1150" y="580" width="80" height="140" />
                    <rect x="1230" y="600" width="140" height="120" />
                    <rect x="1370" y="560" width="60" height="160" />
                    <rect x="1430" y="600" width="90" height="120" />
                </g>
                {/* Faint window lights */}
                <g fill="rgba(255,170,90,0.35)">
                    <rect x="210" y="620" width="3" height="4" />
                    <rect x="240" y="630" width="3" height="4" />
                    <rect x="330" y="590" width="3" height="4" />
                    <rect x="1260" y="620" width="3" height="4" />
                    <rect x="1300" y="640" width="3" height="4" />
                    <rect x="1400" y="590" width="3" height="4" />
                </g>
            </svg>
        </div>
    );
}

function Silhouettes({ phase }) {
    // Two operator silhouettes anchored low in the frame
    const opacity = phase === "in" ? 0.15 : phase === "hold" ? 0.7 : 0.35;
    return (
        <div
            className="absolute pointer-events-none"
            style={{
                bottom: 0,
                left: 0,
                right: 0,
                height: "40%",
                opacity,
                transition: "opacity 1400ms ease",
            }}
        >
            <svg
                width="100%"
                height="100%"
                viewBox="0 0 1600 700"
                preserveAspectRatio="xMidYMax slice"
            >
                {/* Left operator */}
                <g fill="#000" opacity="0.92">
                    {/* body */}
                    <path d="M170,700 C170,560 210,470 250,460 C295,450 340,470 350,540 L360,700 Z" />
                    {/* head */}
                    <circle cx="270" cy="430" r="30" />
                    {/* helmet top */}
                    <path d="M240,410 Q270,388 300,410 L300,435 L240,435 Z" />
                    {/* rifle held forward */}
                    <rect x="290" y="500" width="140" height="10" transform="rotate(-8 290 500)" />
                    <rect x="410" y="493" width="24" height="10" transform="rotate(-8 410 500)" />
                </g>
                {/* Right operator */}
                <g fill="#000" opacity="0.9">
                    <path d="M1240,700 C1240,560 1280,470 1320,460 C1365,450 1410,470 1420,540 L1430,700 Z" />
                    <circle cx="1340" cy="430" r="30" />
                    <path d="M1310,410 Q1340,388 1370,410 L1370,435 L1310,435 Z" />
                    {/* rifle held to shoulder */}
                    <rect x="1170" y="500" width="140" height="10" transform="rotate(8 1310 505)" />
                    <rect x="1146" y="493" width="24" height="10" transform="rotate(8 1310 505)" />
                </g>
                {/* Rim light behind them */}
                <g stroke="rgba(255,255,255,0.08)" strokeWidth="1" fill="none">
                    <path d="M170,700 C170,560 210,470 250,460" />
                    <path d="M1420,700 C1420,560 1380,470 1340,460" />
                </g>
            </svg>
        </div>
    );
}
